import { v4 as uuidv4 } from 'uuid'
import { isArray, isNotEmptyObj, localizeUnixDate } from '@/helpers/helpers'

/**
 * Формирует данные для дополнительных параметров при запросе на проверку подписи
 * @param {Object} params
 * @constructor
 */
export function VerifyPackageRequest (params) {
  this.SignatureType = params.format ?? ''
  this.VerifyParams = {}

  if (params.includeChain === 'true') {
    this.VerifyParams.IncludeChain = 'true'
  }

  if (params.format === 'CMS' || params.format === 'GOST3410') {
    this.VerifyParams.Hash = params.sigType === 'hashSign' ? 'true' : 'false'

    if (
      (params.format === 'CMS' && params.sigType === 'hashSign') ||
      params.format === 'GOST3410'
    ) {
      this.VerifyParams.HashAlgorithm = params.algorithm
    }
  }

  if (!Object.keys(this.VerifyParams).length) {
    delete this.VerifyParams
  }

  if (params.certificate) {
    this.Certificate = params.certificate
  }

  if (isArray(params.plugins)) {
    const activatedPlugins = params.plugins.filter(plugin => plugin.value)

    if (activatedPlugins.length) {
      this.CertVerifiersPluginsIds = activatedPlugins.map(plugin => plugin.id)
    }
  }
}

/**
 * Формирует данные при запросе на проверку сертификата
 * @param {String} base64File
 * @param {Object[]} options
 * @param {Object[]} additionalSettings
 * @constructor
 */
export function CertificateRequest (base64File, options, additionalSettings = []) {
  this.Content = base64File

  if (isArray(options)) {
    const activatedPlugins = options.filter(plugin => plugin.value)

    if (activatedPlugins.length) {
      this.CertVerifiersPluginsIds = activatedPlugins.map(plugin => plugin.id)
    }
  }

  if (isArray(additionalSettings)) {
    this.VerifyParams = {}

    for (const setting of additionalSettings) {
      if (Object.hasOwn(setting, 'name') && Object.hasOwn(setting, 'value')) {
        this.VerifyParams[setting.name] = setting.value.toString()
      }
    }

    if (!Object.keys(this.VerifyParams).length) {
      delete this.VerifyParams
    }
  }
}

/**
 * Обертка ответа сервера при верификации
 * @param {Object|null} successResponse
 * @param {String|null} errorMsg
 * @return {Object}
 * @constructor
 */
export function VerificationResponse ({ successResponse = null, errorMsg = null }) {
  return {
    result: !!successResponse,
    data: successResponse?.data ?? null,
    errorMsg
  }
}

/**
 * Маппер информации о сертификате (SignerCertificateInfo)
 * @param {Object} responseData
 * @return {object[]}
 */

function mapCertificateInfo (responseData) {
  const info = responseData?.SignerCertificateInfo

  if (!info) {
    return null
  }

  const result = [
    { key: 'Субъект', value: info.SubjectName || 'Неизвестно' },
    { key: 'Издатель', value: info.IssuerName || 'Неизвестно' },
    { key: 'Серийный номер', value: info.SerialNumber || 'Неизвестно' },
    {
      key: 'Срок действия',
      value: `${info.NotBeforeUTC ? localizeUnixDate(info.NotBeforeUTC) : info.NotBefore}
        - ${info.NotAfterUTC ? localizeUnixDate(info.NotAfterUTC) : info.NotAfter}`
    }
  ]

  if (info.PkNotBeforeUTC && info.PkNotAfterUTC) {
    result.push(
      {
        key: 'Срок действия закрытого ключа',
        value: `${localizeUnixDate(info.PkNotBeforeUTC)} - ${localizeUnixDate(info.PkNotAfterUTC)}`
      }
    )
  }

  result.push({ key: 'Отпечаток сертификата', value: info.Thumbprint || 'Неизвестно' })

  if (info.RevocationReasonDisplayName) {
    result.push(
      { key: 'Причина отзыва', value: info.RevocationReasonDisplayName, status: false }
    )
  }

  if (info.CertificateRevocationDateUtc) {
    result.push(
      { key: 'Время отзыва', value: localizeUnixDate(info.CertificateRevocationDateUtc), status: false }
    )
  }

  return result
}

/**
 * Маппер информации о дополнительных сертификатов (AdditionalCertificatesInfo)
 * @param {Object} responseData
 * @return {object[]}
 */

function mapAdditionalCertificateInfo (responseData) {
  const certArr = responseData?.AdditionalCertificatesInfo

  if (!certArr?.length) {
    return []
  }

  return certArr.map((info, i) => {
    const certInfo = [
      { key: 'Субъект', value: info.SubjectName || 'Неизвестно' },
      { key: 'Издатель', value: info.IssuerName || 'Неизвестно' },
      { key: 'Серийный номер', value: info.SerialNumber || 'Неизвестно' },
      {
        key: 'Срок действия',
        value: `${info.NotBeforeUTC ? localizeUnixDate(info.NotBeforeUTC) : info.NotBefore}
        - ${info.NotAfterUTC ? localizeUnixDate(info.NotAfterUTC) : info.NotAfter}`
      }
    ]

    if (info.PkNotBeforeUTC && info.PkNotAfterUTC) {
      certInfo.push(
        {
          key: 'Срок действия закрытого ключа',
          value: `${localizeUnixDate(info.PkNotBeforeUTC)} - ${localizeUnixDate(info.PkNotAfterUTC)}`
        }
      )
    }

    certInfo.push({ key: 'Отпечаток сертификата', value: info.Thumbprint || 'Неизвестно' })

    if (info.RevocationReasonDisplayName) {
      certInfo.push(
        { key: 'Причина отзыва', value: info.RevocationReasonDisplayName, status: false }
      )
    }

    if (info.CertificateRevocationDateUtc) {
      certInfo.push(
        { key: 'Время отзыва', value: localizeUnixDate(info.CertificateRevocationDateUtc), status: false }
      )
    }

    const downloadArr = () => {
      return info.CertContent?.length
        ? [{
            fileBase64: info.CertContent,
            title: 'Сертификат',
            mime: 'application/pkix-cert',
            name: `additional_cert_${i + 1}.cer`
          }]
        : []
    }

    return {
      id: uuidv4() || '0',
      info: certInfo,
      title: info.CertDisplayName || `Дополнительный сертификат ${i + 1}`,
      download: downloadArr()
    }
  })
}

/**
 * Маппер информации о проверке плагина (AdditionalCertificateResult)
 * @param {Object} responseData
 * @return {object[]}
 */

function mapPluginInfo (responseData) {
  if (!responseData?.AdditionalCertificateResult?.length) {
    return null
  }

  return responseData.AdditionalCertificateResult.map(resultArr => {
    const result = {
      title: resultArr.PluginDescription,
      id: resultArr.AssemblyName,
      data: [
        {
          key: 'Результат проверки',
          value: resultArr.bResult ? 'Сертификат прошел проверку' : 'Сертификат не прошел проверку',
          status: resultArr.bResult
        }
      ]
    }

    if (isArray(resultArr.ErrorsList)) {
      result.data.push({ key: 'Ошибки', value: resultArr.ErrorsList })
    }

    return result
  })
}

/**
 * Реконструирует данные результатов проверки подписи из ответа
 * @param {VerificationResponse} responseData
 * return {Object[]|null}
 * @constructor
 */
export function VerificationResultSignature (responseData) {
  if (!responseData.result) {
    console.error('Неожиданный формат данных при трансформации результатов проверки')
    return null
  }

  const resultArr = responseData.data.Results

  if (!Array.isArray(resultArr) || !resultArr.length) {
    console.error('При трансформации результатов не найден массив с данными')
    return null
  }

  return resultArr.map(allResults => {
    const signatureResults = allResults.VerificationResults.map(result => {
      const main = () => {
        if (!result) {
          return null
        }

        const mainArr = [
          {
            key: 'Результат проверки',
            value: result.Result ? 'Подпись действительна' : 'Подпись недействительна',
            status: result.Result
          }
          // { key: 'Документ', value: allResults.SignedDocumentName || 'Не указан' }
        ]

        if (allResults.OriginalDocumentName) {
          mainArr.push({ key: 'Исходный документ', value: allResults.OriginalDocumentName })
        }

        if (result.Message) {
          mainArr.push({
            key: 'Дополнительная информация',
            value: result.Message,
            status: result.Result ? null : false
          })
        }

        return mainArr
      }

      const signature = () => {
        const info = result?.SignatureInfo

        if (!info) {
          return null
        }

        const mainArr = []

        if (Object.hasOwn(info, 'XadesType')) {
          mainArr.push({
            key: 'Формат подписи',
            value: `XAdES-${info.XadesType}`
          })
        } else if (Object.hasOwn(info, 'CAdESType')) {
          mainArr.push({
            key: 'Формат подписи',
            value: info.CAdESType === 'PKCS7'
              ? 'PKCS7'
              : `CAdES-${info.CAdESType}`
          })
        }

        if (info.SigningTimeUTC || info.SigningTime) {
          mainArr.push({
            key: 'Время подписи, полученное из штампа',
            value: info.SigningTimeUTC ? localizeUnixDate(info.SigningTimeUTC) : info.SigningTime
          })
        }

        if (info.LocalSigningTimeUTC || info.LocalSigningTime) {
          mainArr.push({
            key: 'Время подписи',
            value: info.LocalSigningTimeUTC ? localizeUnixDate(info.LocalSigningTimeUTC) : info.LocalSigningTime
          })
        }

        if (info.ArchiveSigningTimeUtc || info.ArchiveSigningTime) {
          mainArr.push({
            key: 'Архивный штамп времени',
            value: info.ArchiveSigningTimeUtc ? localizeUnixDate(info.ArchiveSigningTimeUtc) : info.ArchiveSigningTime
          })
        }

        return mainArr.length ? mainArr : null
      }

      const downloadArr = () => {
        const arr = []

        if (result?.SignerCertificate?.length) {
          arr.push({
            fileBase64: result?.SignerCertificate,
            title: 'Сертификат',
            mime: 'application/pkix-cert',
            name: 'signing_cert.cer'
          })
        }

        if (result?.SignerCertificateInfo?.CertificateChain?.length) {
          arr.push({
            fileBase64: result?.SignerCertificateInfo?.CertificateChain,
            title: 'Цепочка сертификатов',
            mime: 'application/x-pkcs7-certificates',
            name: 'cert_chain.p7b'
          })
        }

        return arr
      }

      return {
        id: uuidv4() || '0',
        status: result?.Result,
        info: {
          main: main(),
          signature: signature(),
          certificate: mapCertificateInfo(result),
          additionalCertificates: mapAdditionalCertificateInfo(result),
          plugins: mapPluginInfo(result)
        },
        download: downloadArr()
      }
    })

    return {
      titleMain: allResults.SignedDocumentName || null,
      titleDoc: allResults.OriginalDocumentName || null,
      docIdToDownload: allResults.CanExtractContent,
      id: uuidv4() || '0',
      signatureResults
    }
  })
}

/**
 * Реконструирует данные результатов проверки сертификата из ответа
 * @param {VerificationResponse} responseData
 * @param {String} title
 * return {Object[]|null}
 * @constructor
 */
export function VerificationResultCertificate (responseData, title) {
  if (!responseData.result) {
    console.error('Неожиданный формат данных при трансформации результатов проверки')
    return null
  }

  const resultArr = responseData.data

  const main = () => {
    if (!resultArr) {
      return null
    }

    const mainArr = [
      {
        key: 'Результат проверки',
        value: resultArr.Result ? 'Сертификат прошел проверку' : 'Сертификат не прошел проверку',
        status: resultArr.Result
      },
      { key: 'Название документа', value: title || 'Не указан' }
    ]

    if (resultArr.Message) {
      mainArr.push({
        key: 'Дополнительная информация',
        value: resultArr.Message,
        status: resultArr.Result ? null : false
      })
    }

    return mainArr
  }

  const downloadArr = () => {
    return resultArr?.SignerCertificateInfo?.CertificateChain?.length
      ? [{
          fileBase64: resultArr?.SignerCertificateInfo.CertificateChain,
          title: 'Цепочка сертификатов',
          mime: 'application/x-pkcs7-certificates',
          name: 'cert_chain.p7b'
        }]
      : []
  }

  return {
    titleMain: title || 'Не указан',
    id: uuidv4() || '0',
    status: resultArr?.Result,
    info: {
      main: main(),
      certificate: mapCertificateInfo(resultArr),
      plugins: mapPluginInfo(resultArr)
    },
    download: downloadArr()
  }
}

/**
 * Маппер сообщения об ошибке
 * @param {Object|String} axiosError
 * @return {String}
 */
export function ErrorMessage (axiosError) {
  if (!axiosError) {
    return 'empty'
  }

  if (isNotEmptyObj(axiosError) && axiosError.Error) {
    return axiosError.Error
  }

  if (!axiosError.response?.data) {
    if (axiosError.Message) {
      return axiosError.Message
    }

    if (axiosError.title && axiosError.type) {
      return `${axiosError.title} Error from ${axiosError.type}`
    }

    if (axiosError.message && axiosError.code) {
      return `${axiosError.message}. Code: ${axiosError.code}`
    }

    return JSON.stringify(axiosError)
  }

  const data = axiosError.response.data

  if (isNotEmptyObj(data.errors)) {
    Object.values(data.errors).forEach(errorArr => {
      if (isArray(errorArr)) {
        errorArr.forEach(error => console.error(`Ошибка! ${error}`))
      } else {
        console.error(`Ошибка! ${errorArr}`)
      }
    })
  }

  if (!data.ErrorDescription && !data.Error) {
    return data.Message || data.title || 'error'
  }

  return `${data.ErrorDescription} (${data.Error})`
}
